import { useState } from 'react';
import { sleep } from 'helpers/sleep.ts';
import { QueryFactory } from 'services/api';
import { ExportStatus } from 'services/api/api-client.types.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation';
import { showErrorToast } from 'components/uikit/toast/showToast';

export type UseDocumentSigningStartProps = {
  projectId: number;
  revisionId: number;
  documentIds: number[];
  onFinished?: () => void;
};

export const useDocumentSigningStart = (
  props: UseDocumentSigningStartProps,
) => {
  const [isStarting, setIsStarting] = useState(false);
  const queryClient = useQueryClient();
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation({
    revisionId: props.revisionId,
  });

  async function start() {
    if (!props.revisionId || !props.projectId || isStarting) {
      return;
    }

    setIsStarting(true);
    try {
      //update folders structure before exporting
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.RevisionQuery.getQueryKey(props.revisionId),
      });

      await QueryFactory.DocumentSigningClient.startRevisionSigning(
        props.revisionId,
        props.documentIds,
      );

      while (true) {
        await sleep(1000);
        const revision = await QueryFactory.RevisionClient.get(
          props.revisionId,
        );
        if (revision.sharePointExportStatus === ExportStatus.NotStarted) {
          break;
        }
      }

      await invalidateSignatureBlockQueries();
      props.onFinished?.();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setIsStarting(false);
    }
  }

  return {
    isStarting,
    start,
  };
};
